import { has } from 'lodash';
import ClientApiStrategyTypes from './ClientApiStrategyTypes';

// The screens a step type draws, in order. Strings, not components, so the
// offer gate can read this without pulling React in; ClientApiJourney maps an
// id to its component.
const ClientApiScreens = {
  LOCATION_LIST: 'LOCATION_LIST',
  ADDRESS_LIST: 'ADDRESS_LIST',
  SOLAR_POTENTIAL: 'SOLAR_POTENTIAL',
  POST_SUBMISSION_QUESTIONS: 'POST_SUBMISSION_QUESTIONS',
  SLOT_PICKER: 'SLOT_PICKER',
  SLOTS_BY_DAY: 'SLOTS_BY_DAY',
  REVIEW_BOOKING: 'REVIEW_BOOKING',
  CONFIRMATION: 'CONFIRMATION',
};

// A type absent here is a journey we must not offer, not a blank screen. Rhino
// also publishes SimpleValueList; that is not ours yet.
const SCREENS_BY_TYPE = {
  [ClientApiStrategyTypes.LOCATION_LIST]: [ClientApiScreens.LOCATION_LIST],
  [ClientApiStrategyTypes.SOLAR_POTENTIAL]: [ClientApiScreens.SOLAR_POTENTIAL],
  [ClientApiStrategyTypes.POST_SUBMISSION_QUESTIONS]: [
    ClientApiScreens.POST_SUBMISSION_QUESTIONS,
  ],
  [ClientApiStrategyTypes.APPOINTMENT_PICKING]: [
    ClientApiScreens.SLOT_PICKER,
    ClientApiScreens.REVIEW_BOOKING,
    ClientApiScreens.CONFIRMATION,
  ],
};

export const getStepScreens = (type) => SCREENS_BY_TYPE[type];

export const isSupportedStepType = (type) => has(SCREENS_BY_TYPE, type);

// Capture sets `component` to pick a screen for a step, so solar draws an
// address list where Amplifon draws a store list from the same LocationList
// type. The value is the screen id Chameleon uses, sent verbatim.
const SCREENS_BY_COMPONENT = {
  [ClientApiScreens.ADDRESS_LIST]: [ClientApiScreens.ADDRESS_LIST],
  [ClientApiScreens.SLOTS_BY_DAY]: [
    ClientApiScreens.SLOTS_BY_DAY,
    ClientApiScreens.REVIEW_BOOKING,
    ClientApiScreens.CONFIRMATION,
  ],
};

const hasComponent = (component) => component != null && component !== '';

export const isUnknownComponent = (implementation) =>
  hasComponent(implementation?.component) &&
  !has(SCREENS_BY_COMPONENT, implementation.component);

// Capture may name a component this build does not have yet, because Capture and
// Chameleon deploy apart. An unknown component falls back to the type's default
// screens, or skips the step (empty) when the type has no default, rather than
// dropping the whole journey.
export const getImplementationScreens = (implementation) => {
  const type = implementation?.type;
  const component = implementation?.component;

  if (!hasComponent(component)) return getStepScreens(type);
  if (has(SCREENS_BY_COMPONENT, component))
    return SCREENS_BY_COMPONENT[component];

  return isSupportedStepType(type) ? getStepScreens(type) : [];
};

export const isSupportedImplementation = (implementation) =>
  getImplementationScreens(implementation) !== undefined;

// The screens after which the journey is over, so the inactivity timer stops.
const JOURNEY_END_SCREENS = [ClientApiScreens.CONFIRMATION];

export const isJourneyEndScreen = (screenId) =>
  JOURNEY_END_SCREENS.includes(screenId);

export default ClientApiScreens;
